import { type Control, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { AddFreight } from '#components/order/CreateOrderDialog/AddFreight.tsx';
import { CustomerInput } from '#components/order/CreateOrderDialog/CustomerInput.tsx';
import { LocationForm } from '#components/order/CreateOrderDialog/LocationForm.tsx';
import { OrderTitleInput } from '#components/order/CreateOrderDialog/OrderTitleInput.tsx';
import { dateFieldToJSDate } from '#components/order/CreateOrderDialog/timeWindowFields.ts';
import type { ImportOrderFormData } from '#features/csvImport/ImportOrderFormValidation.ts';

type ImportOrderDetailsProps = {
    control: Control<ImportOrderFormData>;
    index: number;
};

export const ImportOrderDetails = ({ control, index }: ImportOrderDetailsProps) => {
    const intl = useIntl();

    const { watch } = useFormContext<ImportOrderFormData>();
    const orderPrefix = `orders.${index}` as const;
    const orderNumber = watch(`${orderPrefix}.number`);
    const loadingStop = watch(`${orderPrefix}.loadingStop`);
    const loadingStartTarget = loadingStop?.startDate ?? '';

    const pickUpLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.pick-up',
        defaultMessage: 'Pick up',
    });
    const deliveryLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.delivery',
        defaultMessage: 'Delivery',
    });
    const orderLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.orders.csv-import.order-label',
        defaultMessage: 'Order',
    });

    return (
        <div className='panel panel-default margin-bottom-15'>
            <div className='panel-heading display-flex align-items-center justify-content-between gap-10'>
                <div className='text-medium'>
                    {orderLabel} {index + 1}
                </div>
                {orderNumber ? <div className='text-muted'>{orderNumber}</div> : null}
            </div>
            <div className='panel-body padding-15'>
                <div className='display-grid grid-cols-3-sm column-gap-20-sm padding-bottom-10 align-items-end'>
                    <OrderTitleInput
                        intl={intl}
                        control={control}
                        orderNumberPrefix={orderPrefix}
                        showGenerateButton={false}
                    />
                    <CustomerInput intl={intl} control={control} namePrefix={orderPrefix} />
                </div>

                <div className='display-grid grid-cols-2 column-gap-20-sm align-items-end'>
                    <div className='grid-colspan-1'>
                        <div className='text-color-gray text-size-16'>{pickUpLabel}</div>
                        <LocationForm addressType={'loadingStop'} locationPrefix={orderPrefix} type={'import-modal'} />
                    </div>

                    <div className='grid-colspan-1'>
                        <div className='text-color-gray text-size-16'>{deliveryLabel}</div>
                        <LocationForm
                            addressType={'unloadingStop'}
                            locationPrefix={orderPrefix}
                            type={'import-modal'}
                            minDate={dateFieldToJSDate(loadingStartTarget)}
                        />
                    </div>
                </div>
                <div className='padding-top-10'>
                    <AddFreight state={'editOrder'} namePrefix={orderPrefix} />
                </div>
            </div>
        </div>
    );
};
